import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';
import { ConnectorsApi } from 'app/domains/aya/apis/connectors.api';
import {
  ConnectorOauthService,
  IOauthResult,
} from 'app/domains/aya/services/connector-oauth.service';
import { EnumOAuthAuthorizationStatus } from 'app/domains/aya/enums/oauth-authorization-status.enum';
import { EnumPreferencesSection } from 'app/domains/aya/enums/preferences-section.enum';
import { IConnection } from 'app/domains/aya/interfaces/connection.interface';
import { IConnector } from 'app/domains/aya/interfaces/connector.interface';

const SKELETON_COUNT = 4;
const CONNECTED_STATUS = 'active';

const CONNECT_ERROR_MESSAGES: { [code: string]: string } = {
  connector_already_connected: 'Este serviço já está conectado.',
  connector_account_limit_reached:
    'Você atingiu o limite de contas para este serviço.',
  invalid_auth_state:
    'A autorização expirou. Tente conectar novamente.',
  invalid_return_origin:
    'Não foi possível concluir a conexão neste ambiente.',
  invalid_return_path:
    'Não foi possível concluir a conexão neste ambiente.',
  connector_provider_error:
    'O serviço está indisponível no momento. Tente novamente em instantes.',
};

interface IConnectorCard {
  connector: IConnector;
  connection: IConnection | null;
  isConnected: boolean;
  isBusy: boolean;
}

interface IConnectorGroup {
  label: string;
  items: IConnectorCard[];
}

@Component({
  selector: 'sgt-connectors-section',
  templateUrl: './connectors-section.component.html',
  styleUrls: ['./connectors-section.component.scss'],
})
export class ConnectorsSectionComponent implements OnInit, OnDestroy {
  cards: IConnectorCard[] = [];
  groups: IConnectorGroup[] = [];
  isLoading = false;

  readonly skeletonCards = Array.from({ length: SKELETON_COUNT }, (_, index) => index);

  private destroy$ = new Subject<void>();

  constructor(
    private connectorsApi: ConnectorsApi,
    private connectorOauthService: ConnectorOauthService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.handleOauthReturn();
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  actionLabel(card: IConnectorCard): string {
    if (card.isBusy) {
      return card.isConnected ? 'Desconectando…' : 'Conectando…';
    }
    return card.isConnected ? 'Desconectar' : 'Conectar';
  }

  connect(card: IConnectorCard): void {
    const popup = this.connectorOauthService.openPopup();
    if (!popup) {
      this.toastService.error(
        'Permita pop-ups para este site e tente conectar novamente.',
      );
      return;
    }

    card.isBusy = true;

    this.connectorOauthService
      .start(card.connector.slug, this.returnPath(), popup)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          card.isBusy = false;
          this.applyOauthResult(result, card);
        },
        error: error => {
          card.isBusy = false;
          this.toastService.error(this.connectErrorMessage(error, card.connector.name));
        },
      });
  }

  private applyOauthResult(result: IOauthResult, card: IConnectorCard): void {
    const name = card.connector.name;
    if (result.status === EnumOAuthAuthorizationStatus.Connected) {
      this.toastService.success(`${name} conectado.`);
      this.markConnected(card, result.connectionId);
      this.revalidate();
      return;
    }
    if (result.status === EnumOAuthAuthorizationStatus.Cancelled) {
      return;
    }
    this.toastService.error(
      (result.connectorError && CONNECT_ERROR_MESSAGES[result.connectorError]) ||
        `Não foi possível conectar ${name}.`,
    );
    this.revalidate();
  }

  private markConnected(card: IConnectorCard, connectionId: string): void {
    card.connection = {
      id: connectionId,
      connectorSlug: card.connector.slug,
      connectorName: card.connector.name,
      alias: null,
      status: 'active',
      enabled: true,
      authMethod: null,
      createdAt: null,
      updatedAt: null,
    };
    card.isConnected = true;
    this.groupCards();
  }

  disconnect(card: IConnectorCard): void {
    if (!card.connection) {
      return;
    }
    card.isBusy = true;

    this.connectorsApi
      .deleteConnection(card.connection.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          card.isBusy = false;
          card.connection = null;
          card.isConnected = false;
          this.groupCards();
          this.toastService.success(`${card.connector.name} desconectado.`);
        },
        error: error => {
          card.isBusy = false;
          this.toastService.error(
            error?.error?.detail ||
              `Não foi possível desconectar ${card.connector.name}.`,
          );
        },
      });
  }

  private handleOauthReturn(): void {
    const params = this.route.snapshot.queryParamMap;
    const status = params.get('connector_status');

    if (!status) {
      return;
    }

    if (status === 'connected') {
      this.toastService.success('Serviço conectado.');
    } else {
      const code = params.get('connector_error');
      this.toastService.error(
        (code && CONNECT_ERROR_MESSAGES[code]) ||
          'Não foi possível concluir a conexão.',
      );
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private load(): void {
    this.isLoading = true;

    forkJoin({
      catalog: this.connectorsApi.getCatalog(),
      connections: this.connectorsApi.getConnections(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ catalog, connections }) => {
          this.cards = this.buildCards(catalog.items, connections);
          this.groupCards();
          this.isLoading = false;
        },
        error: error => {
          this.isLoading = false;
          this.cards = [];
          this.groupCards();
          this.toastService.error(
            error?.error?.detail || 'Não foi possível carregar os conectores.',
          );
        },
      });
  }

  private revalidate(): void {
    this.connectorsApi
      .getConnections()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: connections => this.reconcileConnections(connections),
        error: () => undefined,
      });
  }

  private reconcileConnections(connections: IConnection[]): void {
    this.cards.forEach(card => {
      const connection = this.findConnection(connections, card.connector.slug);
      card.connection = connection;
      card.isConnected = this.isConnected(connection);
    });
    this.groupCards();
  }

  private groupCards(): void {
    const connected = this.cards.filter(card => card.isConnected);
    const available = this.cards.filter(card => !card.isConnected);

    this.groups = [
      { label: 'Conectadas', items: connected },
      { label: 'Disponíveis', items: available },
    ].filter(group => group.items.length > 0);
  }

  private buildCards(
    catalogItems: IConnector[],
    connections: IConnection[],
  ): IConnectorCard[] {
    return catalogItems.map(connector => {
      const connection = this.findConnection(connections, connector.slug);
      return {
        connector,
        connection,
        isConnected: this.isConnected(connection),
        isBusy: false,
      };
    });
  }

  private findConnection(connections: IConnection[], slug: string): IConnection | null {
    const target = slug.toLowerCase();
    return connections.find(item => item.connectorSlug.toLowerCase() === target) ?? null;
  }

  private isConnected(connection: IConnection | null): boolean {
    return (
      !!connection &&
      connection.status?.toLowerCase() === CONNECTED_STATUS &&
      connection.enabled
    );
  }

  private connectErrorMessage(
    error: { error?: { code?: string; detail?: string } },
    connectorName: string,
  ): string {
    const code = error?.error?.code;
    return (
      (code && CONNECT_ERROR_MESSAGES[code]) ||
      error?.error?.detail ||
      `Não foi possível conectar ${connectorName}.`
    );
  }

  private returnPath(): string {
    return `/ceppem-ai?preferencias=${EnumPreferencesSection.Conectores}`;
  }
}
